import { useEffect, useState, useMemo, useRef, useReducer } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { adminApi } from "../services/adminApi";

import type {
  Prompt,
  PromptVersionHistory,
  PromptVersion,
  CreatePromptRequest,
  PromptEditTurn,
  PromptVersionConflict,
} from "../types/admin";
import { MenuIcon, MoveLeft, Sparkles, Undo2, Trash2 } from "lucide-react";
import { html as diffHtml } from 'diff2html/lib-esm/diff2html';
import 'diff2html/bundles/css/diff2html.min.css';
import { parseDiffFromFile } from "@pierre/diffs";
import { FileDiff } from "@pierre/diffs/react";
import { workingDraftReducer, initWorkingDraft } from "@/lib/promptEdit/workingDraft";
import {
  checkPlaceholderIntegrity,
  describePlaceholderViolations,
} from "@/lib/promptEdit/placeholders";

interface EditorState {
  content: string;
  isDirty: boolean;
  lastSaved: Date | null;
}

interface CreationState {
  identifier: string;
  name: string;
  isActive: boolean;
  isDirty: boolean;
}

export default function PromptEditor() {
  const { identifier } = useParams<{ identifier: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Core state
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [currentPrompt, setCurrentPrompt] = useState<Prompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Creation mode state
  const isCreationMode = identifier === 'new';
  const [creationState, setCreationState] = useState<CreationState>({
    identifier: '',
    name: '',
    isActive: true,
    isDirty: false,
  });
  
  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  
  // Editor state
  const [editorState, setEditorState] = useState<EditorState>({
    content: "",
    isDirty: false,
    lastSaved: null,
  });
  const [saving, setSaving] = useState(false);
  
  // Version management
  const [versionHistory, setVersionHistory] = useState<PromptVersionHistory | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  

  const [compareMode, setCompareMode] = useState(false);
  const [compareFromVersion, setCompareFromVersion] = useState<number | null>(null);
  const [compareToVersion, setCompareToVersion] = useState<number | null>(null);
  const [compareData, setCompareData] = useState<{ from: PromptVersion; to: PromptVersion } | null>(null);
  
  // UI state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showVersionPanel, setShowVersionPanel] = useState(false);
  const [lineNumbers, setLineNumbers] = useState(true);
  const [loadingVersion, setLoadingVersion] = useState(false);
  // ── AI Prompt Edit (inline) ──────────────────────────────
  // The Working Draft is the pure source of truth for the editable template in
  // edit mode (base ↔ current diff, undo/discard). Creation mode and version
  // viewing still use editorState.content.
  const [draft, dispatchDraft] = useReducer(workingDraftReducer, "", initWorkingDraft);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [turns, setTurns] = useState<PromptEditTurn[]>([]);
  const [instruction, setInstruction] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [streamingRationale, setStreamingRationale] = useState("");
  const [aiError, setAiError] = useState<string | null>(null);
  const [editChatId, setEditChatId] = useState<string | null>(null);
  const [draftView, setDraftView] = useState<"editor" | "diff">("editor");

  // Save dialog (changeMessage + placeholder integrity guard)
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [changeMessageInput, setChangeMessageInput] = useState("");
  const [overridePlaceholders, setOverridePlaceholders] = useState(false);

  // Optimistic-concurrency conflict (someone saved underneath this session)
  const [conflict, setConflict] = useState<PromptVersionConflict | null>(null);
  const [conflictTheirTemplate, setConflictTheirTemplate] = useState<string | null>(null);

  // Preview diff state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewGenerating, setPreviewGenerating] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Token estimate state
  const [tokenEstimate, setTokenEstimate] = useState<number | null>(null);
  const [estimatingTokens, setEstimatingTokens] = useState(false);
  const [displayedTokens, setDisplayedTokens] = useState(0);
  const loadingCounterRef = useRef<number | null>(null);
  const settleIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isCreationMode) {
      fetchPrompts();
    } else {
      setLoading(false);
    }
  }, [isCreationMode]);

  useEffect(() => {
    if (!identifier) return;
    
    if (isCreationMode) {
      // Creation mode - setup for new prompt
      setCurrentPrompt(null);
      setEditorState({
        content: "",
        isDirty: false,
        lastSaved: null,
      });
      setVersionHistory(null);
      setSelectedVersion(null);
      setLoadingVersion(false);
      setCompareMode(false);
      setCompareFromVersion(null);
      setCompareToVersion(null);
      setLoading(false);
      setCreationState({
        identifier: '',
        name: '',
        isActive: true,
        isDirty: false,
      });
    } else if (prompts.length > 0) {
      // Edit mode - find existing prompt
      const prompt = prompts.find(p => p.identifier === identifier);
      if (prompt) {
        setCurrentPrompt(prompt);
        setEditorState({
          content: prompt.template || "",
          isDirty: false,
          lastSaved: new Date(prompt.updatedAt),
        });
        // Open a fresh Working Draft based on the active Prompt Version, and
        // capture that version for the optimistic-concurrency lock on Save.
        dispatchDraft({ type: "reset", base: prompt.template || "" });
        setBaseVersion(prompt.version ?? null);
        setTurns([]);
        setEditChatId(null);
        setStreamingRationale("");
        setAiError(null);
        setDraftView("editor");
        setSelectedVersion(null);
        setLoadingVersion(false);
        setCompareMode(false);
        setCompareFromVersion(null);
        setCompareToVersion(null);
        fetchVersionHistory(identifier);
        setEditingName(prompt.name); // Initialize name editing state
      }
    }
  }, [identifier, prompts, isCreationMode]);

  // Reset token estimate state when switching prompts/identifier
  useEffect(() => {
    setTokenEstimate(null);
    setDisplayedTokens(0);
    setEstimatingTokens(false);
    if (loadingCounterRef.current) {
      clearInterval(loadingCounterRef.current);
      loadingCounterRef.current = null;
    }
    if (settleIntervalRef.current) {
      clearInterval(settleIntervalRef.current);
      settleIntervalRef.current = null;
    }
  }, [identifier]);

  // Auto-fetch token estimate when opening an identifier (edit mode)
  useEffect(() => {
    if (identifier && !isCreationMode) {
      handleEstimateTokens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, isCreationMode]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (loadingCounterRef.current) {
        clearInterval(loadingCounterRef.current);
        loadingCounterRef.current = null;
      }
      if (settleIntervalRef.current) {
        clearInterval(settleIntervalRef.current);
        settleIntervalRef.current = null;
      }
    };
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getPrompts();
      setPrompts(response);
    } catch (error) {
      console.error("Failed to fetch prompts:", error);
      setError("Failed to load prompts");
    } finally {
      setLoading(false);
    }
  };

  const fetchVersionHistory = async (promptIdentifier: string) => {
    try {
      const history = await adminApi.getPromptVersions(promptIdentifier);
      setVersionHistory(history);
      
      // Auto-select versions for comparison (current and previous)
      if (history.versions.length >= 2) {
        const sortedVersions = history.versions.sort((a, b) => b.version - a.version);
        setCompareToVersion(sortedVersions[0].version); // Latest/current version
        setCompareFromVersion(sortedVersions[1].version); // Previous version
      }
    } catch (error) {
      console.error("Failed to fetch version history:", error);
    }
  };

  const filteredPrompts = useMemo(() => {
    return prompts.filter((prompt) =>
      searchQuery === "" ||
      prompt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prompt.identifier.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [prompts, searchQuery]);

  // In edit mode the Working Draft reducer owns the editable text; creation mode
  // and version viewing still use editorState.content.
  const isViewingVersion = selectedVersion !== null;
  const editorContent =
    isCreationMode || isViewingVersion ? editorState.content : draft.current;
  const hasDraftChanges = !isCreationMode && draft.current !== draft.base;
  const editorIsDirty = isCreationMode ? editorState.content !== "" : hasDraftChanges;

  const handlePromptSelect = (prompt: Prompt) => {
    if (editorIsDirty) {
      const confirmLeave = confirm("You have unsaved changes. Are you sure you want to leave?");
      if (!confirmLeave) return;
    }

    navigate(`/admin/prompts/editor/${prompt.identifier}`);
  };

  const handleEditorChange = (value: string) => {
    if (isCreationMode) {
      setEditorState(prev => ({ ...prev, content: value, isDirty: value !== "" }));
    } else {
      // A manual edit between AI turns is preserved and becomes the base for the
      // next AI turn (the reducer stacks the prior draft onto history).
      dispatchDraft({ type: "manualEdit", text: value });
    }
  };

  // ── AI Prompt Edit handlers ──────────────────────────────
  const handleRunInstruction = async () => {
    if (!currentPrompt || !instruction.trim() || aiBusy) return;
    const sentInstruction = instruction.trim();
    setAiBusy(true);
    setAiError(null);
    setStreamingRationale("");
    try {
      const result = await adminApi.streamPromptEdit(
        currentPrompt.id,
        {
          query: sentInstruction,
          currentDraft: draft.current,
          identifier: currentPrompt.identifier,
          chatId: editChatId ?? undefined,
          editHistory: turns,
        },
        {
          onRationaleDelta: (text) => setStreamingRationale((prev) => prev + text),
          onChatId: (id) => {
            if (id) setEditChatId(id);
          },
        },
      );
      // Reveal the diff + stage the draft only on completion — a partial
      // template can't be diffed meaningfully (ADR-0046).
      dispatchDraft({ type: "applyAiTurn", template: result.newTemplate });
      setTurns((prev) => [
        ...prev,
        { instruction: sentInstruction, rationale: result.rationale },
      ]);
      setInstruction("");
      setStreamingRationale("");
      setDraftView("diff");
    } catch (err: any) {
      console.error("AI prompt edit failed:", err);
      setAiError(err?.message || "AI edit failed");
    } finally {
      setAiBusy(false);
    }
  };

  const handleUndoTurn = () => {
    dispatchDraft({ type: "undoLastTurn" });
    setTurns((prev) => prev.slice(0, -1));
  };

  const handleDiscardAll = () => {
    dispatchDraft({ type: "discardAll" });
    setTurns([]);
    setStreamingRationale("");
    setAiError(null);
    setDraftView("editor");
  };

  // ── Save (changeMessage + placeholder guard + optimistic lock) ──
  const openSaveDialog = () => {
    if (!currentPrompt || !editorIsDirty) return;
    // Prefill a one-line provenance summary from this session's instructions.
    const summary =
      turns.length > 0
        ? `AI edit: ${turns.map((t) => t.instruction).join("; ")}`.slice(0, 300)
        : "Updated template via editor";
    setChangeMessageInput(summary);
    setOverridePlaceholders(false);
    setShowSaveDialog(true);
  };

  const placeholderVerdict = useMemo(
    () => checkPlaceholderIntegrity(draft.base, draft.current),
    [draft.base, draft.current],
  );

  const handleConfirmSave = async () => {
    if (!currentPrompt) return;
    // Soft-block: a removed/unknown placeholder requires an explicit override.
    if (!placeholderVerdict.ok && !overridePlaceholders) return;

    try {
      setSaving(true);
      setError(null);
      await adminApi.updatePromptTemplate(
        currentPrompt.id,
        draft.current,
        changeMessageInput.trim() || undefined,
        baseVersion ?? undefined,
        // The server re-enforces the placeholder guard; forward the explicit
        // override so an intentionally placeholder-changing save can proceed.
        overridePlaceholders,
      );
      setShowSaveDialog(false);
      setTurns([]);
      setEditChatId(null);
      setDraftView("editor");
      // fetchPrompts re-bases the Working Draft onto the just-saved version.
      await fetchPrompts();
      if (identifier) {
        await fetchVersionHistory(identifier);
        try {
          const r = await adminApi.getPromptTokenSize(identifier);
          const t = r?.totalEstimatedTokens ?? 0;
          setTokenEstimate(t);
          setDisplayedTokens(t);
        } catch {
          // ignore token estimate refresh errors
        }
      }
    } catch (err: any) {
      if (
        err?.response?.status === 409 &&
        err?.response?.data?.code === "PROMPT_VERSION_CONFLICT"
      ) {
        const data = err.response.data as PromptVersionConflict;
        setShowSaveDialog(false);
        setConflict(data);
        try {
          const theirs = await adminApi.getPromptVersion(
            currentPrompt.identifier,
            data.currentVersion,
          );
          setConflictTheirTemplate(theirs.template);
        } catch {
          setConflictTheirTemplate(null);
        }
      } else {
        console.error("Failed to save prompt:", err);
        setError(err?.response?.data?.error || "Failed to save prompt");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardAndRebase = async () => {
    // Discard this session's draft and re-base onto the new active version.
    setConflict(null);
    setConflictTheirTemplate(null);
    setTurns([]);
    setEditChatId(null);
    setDraftView("editor");
    await fetchPrompts();
    if (identifier) await fetchVersionHistory(identifier);
  };

  const handleCreationFieldChange = (field: keyof CreationState, value: any) => {
    setCreationState(prev => ({
      ...prev,
      [field]: value,
      isDirty: true,
    }));
  };

  const handleNameSave = async () => {
    if (!currentPrompt || !editingName.trim() || editingName === currentPrompt.name) {
      setIsEditingName(false);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      await adminApi.updatePrompt(currentPrompt.id, {
        name: editingName.trim(),
      });
      
      // Refresh prompts to get updated name
      await fetchPrompts();
      setIsEditingName(false);
    } catch (error: any) {
      console.error("Failed to update prompt name:", error);
      setError(error.response?.data?.error || "Failed to update prompt name");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (isCreationMode) {
      // Handle prompt creation
      if (!creationState.identifier || !creationState.name || !editorState.content) {
        setError("Please fill in all required fields (identifier, name, and template)");
        return;
      }

      try {
        setSaving(true);
        setError(null);
        
        const createData: CreatePromptRequest = {
          identifier: creationState.identifier,
          name: creationState.name,
          template: editorState.content,
          isActive: creationState.isActive,
        };
        
        const newPrompt = await adminApi.createPrompt(createData);
        
        // Navigate to the new prompt's edit page
        navigate(`/admin/prompts/editor/${newPrompt.identifier}`);
      } catch (error: any) {
        console.error("Failed to create prompt:", error);
        setError(error.response?.data?.error || "Failed to create prompt");
      } finally {
        setSaving(false);
      }
    } else {
      // Edit mode: route through the Save dialog, which gathers the
      // changeMessage, runs the placeholder integrity guard, and sends the
      // captured base version for the optimistic-concurrency lock.
      openSaveDialog();
    }
  };

  const handleVersionSelect = async (version: number) => {
    if (!versionHistory || !identifier) return;
    
    const versionData = versionHistory.versions.find(v => v.version === version);
    if (!versionData) return;

    try {
      setLoadingVersion(true);
      setSelectedVersion(version);
      
      // Fetch the actual version content from API
      const fullVersionData = await adminApi.getPromptVersion(identifier, version);
      
      setEditorState({
        content: fullVersionData.template || "",
        isDirty: false,
        lastSaved: new Date(versionData.createdAt),
      });
      
      // Exit compare mode when selecting a version to view
      setCompareMode(false);
    } catch (error: any) {
      console.error("Failed to fetch version content:", error);
      setError(error.response?.data?.error || "Failed to load version content");
      setSelectedVersion(null);
    } finally {
      setLoadingVersion(false);
    }
  };

  const handleCompareVersions = async () => {
    if (!identifier || compareFromVersion === null || compareToVersion === null) return;

    setLoadingVersion(true);
    try {
      const [from, to] = await Promise.all([
        adminApi.getPromptVersion(identifier, compareFromVersion),
        adminApi.getPromptVersion(identifier, compareToVersion),
      ]);
      setCompareData({ from, to });
      setCompareMode(true);
    } catch (error) {
      console.error("Failed to compare versions:", error);
      setError("Failed to compare versions");
    } finally {
      setLoadingVersion(false);
    }
  };

  const handleRevert = async (version: number) => {
    if (!identifier) return;
    
    const confirmRevert = confirm(`Are you sure you want to revert to version ${version}? This will create a new version.`);
    if (!confirmRevert) return;
    
    try {
      await adminApi.revertPromptToVersion(identifier, version, {
        changeMessage: `Reverted to version ${version}`
      });
      
      await fetchPrompts();
      await fetchVersionHistory(identifier);
      setSelectedVersion(null);
    } catch (error: any) {
      console.error("Failed to revert:", error);
      setError(error.response?.data?.error || "Failed to revert to version");
    }
  };

  const renderLineNumbers = (content: string) => {
    const lines = content.split('\n');
    const maxLineNumber = lines.length;
    const padding = maxLineNumber.toString().length;
    
    return lines.map((_, index) => (
      <div 
        key={index} 
        className="text-gray-400 text-sm font-mono select-none pr-3 text-right border-r border-gray-200"
        style={{ 
          minWidth: `${Math.max(padding * 8 + 16, 40)}px`,
          lineHeight: '1.5rem'
        }}
      >
        {String(index + 1).padStart(padding, ' ')}
      </div>
    ));
  };

  const compareFileDiff = useMemo(() => {
    if (!compareData) return null;
    const fileName = `${identifier ?? "prompt"}.txt`;
    return parseDiffFromFile(
      { name: fileName, contents: compareData.from.template },
      { name: fileName, contents: compareData.to.template },
    );
  }, [compareData, identifier]);

  // Cumulative diff of the Working Draft against the active Prompt Version.
  const draftFileDiff = useMemo(() => {
    if (!hasDraftChanges) return null;
    const fileName = `${identifier ?? "prompt"}.txt`;
    return parseDiffFromFile(
      { name: fileName, contents: draft.base },
      { name: fileName, contents: draft.current },
    );
  }, [hasDraftChanges, draft.base, draft.current, identifier]);

  // On conflict, the intervening change: my base → the colleague's new version.
  const conflictFileDiff = useMemo(() => {
    if (conflictTheirTemplate == null) return null;
    const fileName = `${identifier ?? "prompt"}.txt`;
    return parseDiffFromFile(
      { name: fileName, contents: draft.base },
      { name: fileName, contents: conflictTheirTemplate },
    );
  }, [conflictTheirTemplate, draft.base, identifier]);

  const renderDiffView = () => {
    if (!compareData || !compareFileDiff) return null;

    const linesChanged = compareFileDiff.hunks.reduce(
      (n, h) => n + h.additionLines + h.deletionLines,
      0,
    );
    const nameChanged =
      !!compareData.from.name &&
      !!compareData.to.name &&
      compareData.from.name !== compareData.to.name;

    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Comparing version {compareData.from.version} → {compareData.to.version}
              </h3>
            </div>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-3">
                <span className="flex items-center text-blue-600 font-medium">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  {linesChanged} {linesChanged === 1 ? "line" : "lines"} changed
                </span>
                {nameChanged && (
                  <span className="flex items-center text-orange-600 font-medium">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    Name changed
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCompareMode(false)}
                className="bg-white hover:bg-gray-50"
              >
                Exit Compare
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-white">
          <FileDiff
            fileDiff={compareFileDiff}
            options={{ diffStyle: "split", theme: "github-light", overflow: "wrap" }}
            disableWorkerPool
          />
        </div>
      </div>
    );
  };

  // Build a unified diff between two strings, line-based
  const generateUnifiedDiff = (oldStr: string, newStr: string, fileName: string) => {
    const a = oldStr.split('\n');
    const b = newStr.split('\n');
    const n = a.length;
    const m = b.length;
    const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));

    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
        else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }

    const lines: string[] = [];
    let i = 0, j = 0;
    while (i < n && j < m) {
      if (a[i] === b[j]) {
        lines.push(' ' + a[i]);
        i++; j++;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        lines.push('-' + a[i]);
        i++;
      } else {
        lines.push('+' + b[j]);
        j++;
      }
    }
    while (i < n) { lines.push('-' + a[i]); i++; }
    while (j < m) { lines.push('+' + b[j]); j++; }

    const oldStart = n > 0 ? 1 : 0;
    const newStart = m > 0 ? 1 : 0;
    const header = `--- a/${fileName}\n+++ b/${fileName}\n@@ -${oldStart},${n} +${newStart},${m} @@\n`;
    return header + lines.join('\n') + '\n';
  };

  const openPreview = () => {
    if (selectedVersion !== null || loadingVersion) return;
    try {
      setPreviewGenerating(true);
      setPreviewError(null);
      const before = isCreationMode ? '' : (currentPrompt?.template || '');
      const after = editorContent || '';
      const fileName = (currentPrompt?.identifier || creationState.identifier || 'prompt.txt') + '.txt';
      const unified = generateUnifiedDiff(before, after, fileName);
      const html = diffHtml(unified, {
        // inputFormat: 'diff',
        drawFileList: false,
        matching: 'lines',
        outputFormat: 'line-by-line',
      });
      setPreviewHtml(html);
      setIsPreviewOpen(true);
    } catch (e: any) {
      console.error('Failed to generate preview diff:', e);
      setPreviewError('Failed to generate preview');
    } finally {
      setPreviewGenerating(false);
    }
  };

  // Token estimate helpers
  const startLoadingCounter = (startFrom: number) => {
    setDisplayedTokens(startFrom);
    if (loadingCounterRef.current) clearInterval(loadingCounterRef.current);
    loadingCounterRef.current = window.setInterval(() => {
      setDisplayedTokens(prev => {
        const increment = Math.max(1, Math.floor(prev * 0.02) + 3);
        const cap = (editorContent?.length || 0) * 2 + 100;
        const next = prev + increment;
        return next > cap ? cap : next;
      });
    }, 60);
  };

  const smoothSettleTo = (finalValue: number) => {
    if (loadingCounterRef.current) {
      clearInterval(loadingCounterRef.current);
      loadingCounterRef.current = null;
    }
    if (settleIntervalRef.current) clearInterval(settleIntervalRef.current);
    const durationMs = 400;
    const stepMs = 16;
    const steps = Math.ceil(durationMs / stepMs);
    let currentStep = 0;
    const start = displayedTokens;
    const delta = finalValue - start;
    settleIntervalRef.current = window.setInterval(() => {
      currentStep += 1;
      const progress = Math.min(1, currentStep / steps);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(start + delta * eased);
      setDisplayedTokens(value);
      if (progress >= 1) {
        if (settleIntervalRef.current) {
          clearInterval(settleIntervalRef.current);
          settleIntervalRef.current = null;
        }
      }
    }, stepMs);
  };

  const handleEstimateTokens = async () => {
    if (!identifier || isCreationMode || estimatingTokens) return;
    try {
      setEstimatingTokens(true);
      startLoadingCounter(tokenEstimate ?? 0);
      const res = await adminApi.getPromptTokenSize(identifier);
      const tokens = res?.totalEstimatedTokens ?? 0;
      setTokenEstimate(tokens);
      smoothSettleTo(tokens);
    } catch (err) {
      console.error('Failed to fetch token estimate', err);
      setError('Failed to fetch token estimate');
      if (loadingCounterRef.current) {
        clearInterval(loadingCounterRef.current);
        loadingCounterRef.current = null;
      }
    } finally {
      setEstimatingTokens(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading prompts...</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`bg-white border-r border-gray-200 transition-all duration-300 ${
        sidebarCollapsed ? 'w-16' : 'w-80'
      }`}>
        {!sidebarCollapsed && (
          <div className="h-full flex flex-col">
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Prompts</h2>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSidebarCollapsed(true)}
                >
                  <MoveLeft />
                </Button>
              </div>
              <Input
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
            
            {/* Prompts List */}
            <div className="flex-1 overflow-y-auto space-y-2 p-2">
              {filteredPrompts.map((prompt) => (
                <div
                  key={prompt.id}
                  onClick={() => handlePromptSelect(prompt)}
                  className={`p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
                    prompt.identifier === identifier 
                      ? 'bg-blue-100 border-blue-300 shadow-sm' 
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="font-medium text-sm text-start text-gray-900">{prompt.name}</div>
                  <div className="text-xs text-gray-500 mt-1 font-mono text-start px-2 py-1 rounded">
                    {prompt.identifier}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-xs text-gray-400">
                      {new Date(prompt.updatedAt).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-400">
                      {prompt.template.length.toLocaleString()} chars
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {sidebarCollapsed && (
          <div className="pr-4 py-4">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSidebarCollapsed(false)}
            >
              <MenuIcon />
            </Button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {isCreationMode ? (
                <div>
                  <h1 className="text-2xl text-start font-bold ">Create New Prompt</h1>
                  <p className="text-start text-gray-600">Fill in the details below </p>
                </div>
              ) : currentPrompt ? (
                <div>
                  {isEditingName ? (
                    <div className="flex items-center space-x-2">
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleNameSave();
                          }
                          if (e.key === 'Escape') {
                            setIsEditingName(false);
                            setEditingName(currentPrompt.name);
                          }
                        }}
                        onBlur={handleNameSave}
                        className="text-2xl font-bold border-none shadow-none p-0 h-auto focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleNameSave}
                        disabled={saving}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setIsEditingName(false);
                          setEditingName(currentPrompt.name);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <h1 
                      className="text-2xl text-start font-bold cursor-pointer hover:text-gray-600 transition-colors"
                      onClick={() => setIsEditingName(true)}
                      title="Click to edit prompt name"
                    >
                      {currentPrompt.name}
                    </h1>
                  )}
                  <p className="text-start text-gray-600">{currentPrompt.identifier}</p>
                  {selectedVersion !== null && (
                    <div className="text-start mt-2">
                      <Badge variant="info">Viewing version {selectedVersion}</Badge>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            <div className="flex items-center space-x-3">
              {selectedVersion !== null && (
                <Button
                  variant="outline"
                  onClick={() => {
                    // Return to the in-progress Working Draft (preserve edits).
                    setSelectedVersion(null);
                    setLoadingVersion(false);
                  }}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Back to Current
                </Button>
              )}
              {(editorIsDirty || creationState.isDirty) && (
                <Badge variant="error">Unsaved changes</Badge>
              )}
              {editorState.lastSaved && !isCreationMode && (
                <span className="text-sm text-gray-500">
                  Last saved: {editorState.lastSaved.toLocaleTimeString()}
                </span>
              )}
              {!isCreationMode && (
                <Button
                  variant="outline"
                  onClick={() => setShowVersionPanel(!showVersionPanel)}
                >
                  History
                </Button>
              )}
              {isCreationMode ? (
                editorContent !== "" ? (
                  <Button
                    onClick={openPreview}
                    disabled={saving || loadingVersion}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Preview
                  </Button>
                ) : (
                  <Button
                    onClick={handleSave}
                    disabled={
                      saving ||
                      !creationState.identifier ||
                      !creationState.name ||
                      !editorContent
                    }
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {saving ? "Creating..." : "Create Prompt"}
                  </Button>
                )
              ) : isViewingVersion ? (
                <Button disabled variant="outline">
                  Viewing History
                </Button>
              ) : (
                <Button
                  onClick={openSaveDialog}
                  disabled={saving || loadingVersion || !editorIsDirty}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Creation Form (only in creation mode) */}
        {isCreationMode && (
          <div className="bg-gray-50 border-b border-gray-200 p-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Identifier *
                  </label>
                  <Input
                    value={creationState.identifier}
                    onChange={(e) => handleCreationFieldChange('identifier', e.target.value)}
                    placeholder="unique-prompt-identifier"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <Input
                    value={creationState.name}
                    onChange={(e) => handleCreationFieldChange('name', e.target.value)}
                    placeholder="Prompt display name"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <Select
                    value={creationState.isActive ? "active" : "inactive"}
                    onChange={(e) => handleCreationFieldChange('isActive', e.target.value === 'active')}
                    options={[
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Editor Area */}
        <div className="flex-1 flex">
          {/* Main Editor */}
          <div className="flex-1 flex flex-col">
            {/* Editor Toolbar */}
            <div className={`border-b border-gray-200 px-4 py-2 text-sm flex items-center justify-between ${
              selectedVersion !== null ? 'bg-blue-50' : 'bg-gray-100'
            }`}>
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">
                  {editorContent?.split('\n').length || 0} lines
                </span>
                <span className="text-gray-600">
                  {editorContent?.length || 0} characters
                </span>
                 <span
                  className="text-gray-600 inline-flex items-center rounded px-2 py-1"
                >
                  <span className="mr-1">Token estimate:</span>
                  <span className={`  'text-gray-800'}`}>
                    {estimatingTokens ? displayedTokens : (tokenEstimate ?? '—')}
                  </span>
                </span>
                {loadingVersion && (
                  <span className="text-blue-600 font-medium">
                    Loading version content...
                  </span>
                )}
                {selectedVersion !== null && !loadingVersion && (
                  <span className="text-blue-600 font-medium">
                    Viewing version {selectedVersion}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <label className="flex items-center space-x-1">
                  <input
                    type="checkbox"
                    checked={lineNumbers}
                    onChange={(e) => setLineNumbers(e.target.checked)}
                  />
                  <span>Line numbers</span>
                </label>
                {!isCreationMode && !isViewingVersion && hasDraftChanges && (
                  <div className="inline-flex rounded border border-gray-300 overflow-hidden">
                    <button
                      onClick={() => setDraftView("editor")}
                      className={`px-2 py-1 ${draftView === "editor" ? "bg-blue-600 text-white" : "bg-white text-gray-700"}`}
                    >
                      Editor
                    </button>
                    <button
                      onClick={() => setDraftView("diff")}
                      className={`px-2 py-1 ${draftView === "diff" ? "bg-blue-600 text-white" : "bg-white text-gray-700"}`}
                    >
                      Diff
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Editor Content */}
            <div className="flex-1 overflow-hidden">
              {compareMode ? (
                renderDiffView()
              ) : loadingVersion ? (
                <div className="h-full flex items-center justify-center bg-gray-50">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-gray-600">Loading version content...</p>
                  </div>
                </div>
              ) : !isCreationMode && !isViewingVersion && draftView === "diff" && draftFileDiff ? (
                <div className="h-full overflow-auto bg-white">
                  <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 text-sm text-gray-600">
                    Cumulative changes — Working Draft vs active v{baseVersion}
                  </div>
                  <FileDiff
                    fileDiff={draftFileDiff}
                    options={{ diffStyle: "split", theme: "github-light", overflow: "wrap" }}
                    disableWorkerPool
                  />
                </div>
              ) : (
                <div className="h-full flex bg-white shadow-inner">
                  {lineNumbers && (
                    <div className="bg-gray-50 border-r border-gray-200 px-2 py-4 min-w-[50px] flex flex-col">
                      {renderLineNumbers(editorContent || "")}
                    </div>
                  )}
                  <div className="flex-1 relative">
                    <textarea
                      value={editorContent || ""}
                      onChange={(e) => handleEditorChange(e.target.value)}
                      readOnly={isViewingVersion}
                      className={`w-full h-full p-4 border-none outline-none resize-none font-mono text-sm ${
                        isViewingVersion
                          ? 'bg-blue-50 cursor-default text-gray-700'
                          : 'bg-white text-gray-900 focus:bg-gray-50'
                      } transition-colors duration-200`}
                      style={{
                        lineHeight: '1.5rem',
                        tabSize: 2
                      }}
                      spellCheck={false}
                      placeholder={isViewingVersion
                        ? `Viewing version ${selectedVersion} (read-only)`
                        : "Enter your prompt template here...\n\nTip: Use {variable} for template placeholders"
                      }
                    />
                    {/* Subtle grid overlay for better visual alignment */}
                    <div className="absolute inset-0 pointer-events-none opacity-5">
                      <div className="h-full bg-gradient-to-b from-gray-200 via-transparent to-transparent bg-[length:100%_1.5rem]"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Inline AI Prompt Edit surface (docked under the editor) */}
            {!isCreationMode && !isViewingVersion && !compareMode && (
              <div className="border-t border-gray-200 bg-white">
                {(turns.length > 0 || streamingRationale || aiError) && (
                  <div className="px-4 py-2 max-h-44 overflow-auto space-y-2 border-b border-gray-100">
                    {turns.map((t, i) => (
                      <div key={i} className="text-xs">
                        <div className="text-gray-500">› {t.instruction}</div>
                        <div className="text-gray-700">{t.rationale}</div>
                      </div>
                    ))}
                    {aiBusy && (
                      <div className="text-xs text-blue-700 whitespace-pre-wrap">
                        {streamingRationale || "Thinking…"}
                        <span className="animate-pulse">▍</span>
                      </div>
                    )}
                    {aiError && <div className="text-xs text-red-600">{aiError}</div>}
                  </div>
                )}

                {(turns.length > 0 || hasDraftChanges) && (
                  <div className="px-4 py-2 flex items-center gap-2 border-b border-gray-100">
                    {draft.history.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleUndoTurn}
                        disabled={aiBusy}
                      >
                        <Undo2 className="w-3 h-3 mr-1" /> Undo last turn
                      </Button>
                    )}
                    {hasDraftChanges && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleDiscardAll}
                        disabled={aiBusy}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Discard all
                      </Button>
                    )}
                  </div>
                )}

                <div className="px-4 py-3 flex items-end gap-2">
                  <textarea
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        handleRunInstruction();
                      }
                    }}
                    rows={2}
                    disabled={aiBusy}
                    placeholder="Tell the AI how to revise this prompt…  (⌘/Ctrl+Enter to run)"
                    className="flex-1 resize-none rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                  />
                  <Button
                    onClick={handleRunInstruction}
                    disabled={aiBusy || !instruction.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    {aiBusy ? "Editing…" : "Edit with AI"}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {!isCreationMode && showVersionPanel && versionHistory && (
            <div className="w-80 border-l bg-white">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-lg font-medium">Version History</h3>
                <p className="text-sm text-gray-600">
                  Current: v{versionHistory.versions[0]?.version}
                </p>
              </div>
              
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Compare from:</label>
                    <Select
                      value={compareFromVersion?.toString() || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCompareFromVersion(value ? Number(value) : null);
                      }}
                      options={[
                        { value: "", label: "Select version..." },
                        ...versionHistory.versions.map(v => ({
                          value: v.version.toString(),
                          label: `v${v.version}${v.isActive ? ' (current)' : ''}`
                        }))
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Compare to:</label>
                    <Select
                      value={compareToVersion?.toString() || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCompareToVersion(value ? Number(value) : null);
                      }}
                      options={[
                        { value: "", label: "Select version..." },
                        ...versionHistory.versions.map(v => ({
                          value: v.version.toString(),
                          label: `v${v.version}${v.isActive ? ' (current)' : ''}`
                        }))
                      ]}
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={handleCompareVersions}
                    disabled={!compareFromVersion || !compareToVersion || compareFromVersion === compareToVersion || loadingVersion}
                    className="w-full"
                  >
                    {!compareFromVersion || !compareToVersion 
                      ? "Select versions to compare" 
                      : compareFromVersion === compareToVersion 
                        ? "Select different versions"
                        : "Compare Versions"
                    }
                  </Button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-2 p-3">
                {versionHistory.versions.map((version) => (
                  <div
                    key={version.id}
                    className={`p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
                      selectedVersion === version.version 
                        ? 'bg-blue-100 border-blue-300 shadow-sm' 
                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
                    } ${loadingVersion ? 'opacity-50 pointer-events-none' : ''}`}
                    onClick={() => handleVersionSelect(version.version)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-semibold text-sm text-gray-900">
                            v{version.version}
                          </span>
                          {version.isActive && (
                            <Badge variant="success" className="text-xs">
                              Current
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mb-1">
                          Updated:
                          <span className="text-gray-700">
                          {new Date(version.createdAt).toLocaleString()}
                            </span> 
                        </div>
                        {version.changeMessage && (
                          <div className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded mt-2">
                            {version.changeMessage}
                          </div>
                        )}
                      </div>
                      {!version.isActive && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRevert(version.version);
                          }}
                          className="text-xs ml-2 text-black-600 border-gray-200 hover:border-gray-500"
                        >
                          Revert
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI-edit Save dialog: changeMessage + placeholder integrity guard */}
      {showSaveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !saving && setShowSaveDialog(false)}
          />
          <div className="relative bg-white w-[90vw] max-w-lg rounded-lg shadow-lg flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Save new version</h3>
              <p className="text-sm text-gray-600">
                Creates v{(baseVersion ?? 0) + 1}, served to every organization.
              </p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Change message</label>
                <textarea
                  value={changeMessageInput}
                  onChange={(e) => setChangeMessageInput(e.target.value)}
                  rows={2}
                  className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="One line on what changed and why"
                />
              </div>
              {!placeholderVerdict.ok && (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 space-y-2">
                  <div className="text-sm font-medium text-amber-800">
                    Placeholder warning
                  </div>
                  <ul className="text-xs text-amber-700 list-disc pl-4 space-y-1">
                    {describePlaceholderViolations(placeholderVerdict).map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                  <label className="flex items-center gap-2 text-xs text-amber-800">
                    <input
                      type="checkbox"
                      checked={overridePlaceholders}
                      onChange={(e) => setOverridePlaceholders(e.target.checked)}
                    />
                    Save anyway (I understand this changes the runtime contract)
                  </label>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowSaveDialog(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSave}
                disabled={saving || (!placeholderVerdict.ok && !overridePlaceholders)}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? "Saving…" : "Save version"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Optimistic-concurrency conflict dialog */}
      {conflict && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white w-[90vw] max-w-3xl max-h-[80vh] rounded-lg shadow-lg flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-red-700">Version conflict</h3>
              <p className="text-sm text-gray-600">
                Someone saved v{conflict.currentVersion} while you were editing (your
                base was v{conflict.baseVersion}). Saving now would overwrite their
                change. Review their version, then discard yours and re-base.
              </p>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {conflictFileDiff ? (
                <FileDiff
                  fileDiff={conflictFileDiff}
                  options={{ diffStyle: "split", theme: "github-light", overflow: "wrap" }}
                  disableWorkerPool
                />
              ) : (
                <div className="text-sm text-gray-500">Loading their version…</div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setConflict(null);
                  setConflictTheirTemplate(null);
                }}
              >
                Keep editing
              </Button>
              <Button
                onClick={handleDiscardAndRebase}
                className="bg-red-600 hover:bg-red-700"
              >
                Discard &amp; re-base
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsPreviewOpen(false)}></div>
          <div className="relative bg-white w-[90vw] max-w-5xl max-h-[80vh] rounded-lg shadow-lg flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Preview changes</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setIsPreviewOpen(false)}>×</button>
            </div>
            <div className="flex-1 overflow-auto">
              <style>{`
                .diff2html--wrapper .d2h-code-linenumber,
                .diff2html--wrapper .d2h-code-side-linenumber,
                .diff2html--wrapper .d2h-info {
                  position: static !important;
                  left: auto !important;
                  right: auto !important;
                  top: auto !important;
                  z-index: auto !important;
                }
              `}</style>
              {previewGenerating ? (
                <div className="p-6 text-gray-600">Generating preview...</div>
              ) : previewError ? (
                <div className="p-6 text-red-600">{previewError}</div>
              ) : (
                <div className="diff2html--wrapper" dangerouslySetInnerHTML={{ __html: previewHtml || '' }} />
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex items-center justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Back</Button>
              <Button
                onClick={async () => {
                  await handleSave();
                  setIsPreviewOpen(false);
                }}
                disabled={saving || (isCreationMode && (!creationState.identifier || !creationState.name || !editorState.content))}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? (isCreationMode ? 'Creating...' : 'Saving...') : (isCreationMode ? 'Create Prompt' : 'Save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 rounded-md p-4 shadow-lg z-50">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 